import { formatShortDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { PAST_SHIFT_DETAILS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import {
  ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatusUpdater,
  type ExtraWorkedTimeRequestStatusUpdater as ExtraWorkedTimeRequestStatusUpdaterType,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { parseISO } from "date-fns";
import { generatePath } from "react-router-dom";

import { type ShiftHistoryBannerCardData } from "./ShiftHistoryBannerCard";

export interface ExtraWorkedTimeRequestInput {
  id: string;
  shiftId: string;
  workplaceName: string;
  workplaceTimezone: string;
  shiftDate: Date;
  updatedAt: string;
  status: ExtraWorkedTimeRequestStatus.APPROVED | ExtraWorkedTimeRequestStatus.DECLINED;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  statusUpdatedBy?: ExtraWorkedTimeRequestStatusUpdaterType;
  statusUpdateReason?: string | null;
}

interface UnverifiedShiftInput {
  shiftId: string;
  workplaceName: string;
  workplaceTimezone?: string;
  shiftDate: Date;
  shiftEndAt: string;
  ctaButtonText: string;
  isLoading?: boolean;
  onCtaClick: () => void;
}

function getExtraWorkedTimeRequestContent(params: {
  isApproved: boolean;
  isExpired: boolean;
  isFullShiftPaymentRequest: boolean;
  workplaceName: string;
  formattedDate: string;
}): { title: string; subtitle: string } {
  const { isApproved, isExpired, isFullShiftPaymentRequest, workplaceName, formattedDate } = params;

  if (isApproved && isFullShiftPaymentRequest) {
    return {
      title: "Good news! Your full shift payment was approved.",
      subtitle: `${workplaceName} approved your full shift payment for your shift on ${formattedDate}.`,
    };
  }

  if (isApproved) {
    return {
      title: "Good news! Your pay adjustment was approved.",
      subtitle: `${workplaceName} approved your pay adjustment request for your shift on ${formattedDate}.`,
    };
  }

  if (isExpired && isFullShiftPaymentRequest) {
    return {
      title: "Your shift payment request expired.",
      subtitle: `Your shift payment request for your ${formattedDate} shift at ${workplaceName} wasn't reviewed and may have been missed.`,
    };
  }

  if (isExpired) {
    return {
      title: "Your pay adjustment request expired.",
      subtitle: `Your pay adjustment request for your ${formattedDate} shift at ${workplaceName} wasn't reviewed and may have been missed.`,
    };
  }

  if (isFullShiftPaymentRequest) {
    return {
      title: "Your shift payment request was declined.",
      subtitle: `${workplaceName} declined your shift payment request for your shift on ${formattedDate}.`,
    };
  }

  return {
    title: "Your pay adjustment request was declined.",
    subtitle: `${workplaceName} declined your pay adjustment request for your shift on ${formattedDate}.`,
  };
}

export function buildExtraWorkedTimeRequestCard(params: {
  request: ExtraWorkedTimeRequestInput;
  onDismiss: () => void;
  onNavigate: (path: string) => void;
}): ShiftHistoryBannerCardData {
  const { request, onDismiss, onNavigate } = params;

  const isApproved = request.status === ExtraWorkedTimeRequestStatus.APPROVED;
  const isExpired =
    !isApproved && request.statusUpdatedBy === ExtraWorkedTimeRequestStatusUpdater.SYSTEM;
  const isFullShiftPaymentRequest = request.isFullShiftPayment ?? !request.isVerified;
  // Replace space with non-breaking space to prevent line break between month and day (e.g. "Mar 20")
  const formattedDate = formatShortDate(request.shiftDate, {
    timeZone: request.workplaceTimezone,
  }).replace(" ", "\u00A0");

  const { title, subtitle } = getExtraWorkedTimeRequestContent({
    isApproved,
    isExpired,
    isFullShiftPaymentRequest,
    workplaceName: request.workplaceName,
    formattedDate,
  });

  const navigateToPastShiftDetails = () => {
    onDismiss();
    onNavigate(generatePath(PAST_SHIFT_DETAILS_FULL_PATH, { shiftId: request.shiftId }));
  };

  return {
    id: request.id,
    sortDate: parseISO(request.updatedAt),
    title,
    subtitle,
    icon: isApproved ? "clock-success-with-dollar-colored" : "clock-error-with-exclamation-colored",
    variant: isApproved ? "success" : "error",
    errorMessage:
      !isApproved && !isExpired && isDefined(request.statusUpdateReason)
        ? request.statusUpdateReason
        : undefined,
    ctaButtonText: isApproved ? "View details" : "View details & resubmit",
    onClick: navigateToPastShiftDetails,
  };
}

export function buildUnverifiedShiftCard(shift: UnverifiedShiftInput): ShiftHistoryBannerCardData {
  // Replace space with non-breaking space to prevent line break between month and day (e.g. "Mar 20")
  const formattedDate = formatShortDate(shift.shiftDate, {
    ...(isDefined(shift.workplaceTimezone) && { timeZone: shift.workplaceTimezone }),
  }).replace(" ", "\u00A0");

  return {
    id: shift.shiftId,
    sortDate: parseISO(shift.shiftEndAt),
    title: `You forgot to clock out on ${formattedDate}.`,
    subtitle: `Please submit your timesheet for your shift at ${shift.workplaceName} to get paid.`,
    icon: "clock-error-with-exclamation-colored",
    variant: "error",
    ctaButtonText: shift.ctaButtonText,
    ctaButtonIsLoading: shift.isLoading,
    onClick: shift.onCtaClick,
  };
}
